import type { NotificationBody, ResourceResponse } from "types";
import { ProcessResponseStrategy } from "consts/api";
import { when } from "util/support";
import { transformErrorResponseIntoMessage } from "util/transformers";
import { NotificationSeverity, type Notification } from "features/general/types";
import { isCodeIncludes } from "features/auth/helpers";
import { ManagementApiResponseAdapter, ManagementApiResponseFactory } from "util/api";
import type { RequestSettings } from "./types";

const FALLBACK_ERROR_MESSAGE = 'Something went wrong. Please try again.';

/**
 * Thunks reject HTTP errors with the raw Response (see payloadCreator).
 * Read its JSON body so the notification shows the API message instead of nothing.
 */
export const toResourceResponse = async (payload: unknown): Promise<ResourceResponse<null>> => {
    if (!(payload instanceof Response)) {
        return payload as ResourceResponse<null>;
    }

    const { ok, status, statusText } = payload;

    try {
        return ManagementApiResponseAdapter.from<null>(await payload.json(), {
            ok,
            status,
            statusText
        });
    } catch {
        return ManagementApiResponseFactory.make({
            success: ok,
            code: status,
            message: statusText
        });
    }
};

export const getNotificationSettings = <T>({
    success,
    data,
    message
}: ResourceResponse<T>): Notification => {
    return when(
        success,
        () => {
            const queuedMessage = (data as NotificationBody)?.message;

            return {
                message: queuedMessage || message,
                severity: when(
                    queuedMessage,
                    NotificationSeverity.Info,
                    NotificationSeverity.Success
                )
            };
        },
        () => ({
            severity: NotificationSeverity.Error,
            message: when(
                data instanceof Object,
                () => transformErrorResponseIntoMessage(data as Record<string, string[]>),
                message
            ) || message || FALLBACK_ERROR_MESSAGE
        })
    );
};

export const withDefaults = <T>(...settings: Array<RequestSettings<T>>) => {
    const {
        shouldNotifyOnSuccess,
        shouldNotifyOnReject,
        ...settingsWithDefaults
    } = {
        processResponseStrategy: ProcessResponseStrategy.Json,
        shouldNotifyOnSuccess: false,
        setLoading: () => { },
        shouldNotifyOnReject: (resourceResponse?: ResourceResponse<T>) => when(
            !resourceResponse?.success,
            () => {
                const { code } = resourceResponse ?? {};

                return !code || !isCodeIncludes(code);
            }
        ),
        onSuccess: (_: ResourceResponse<T>) => { },
        onError: (_: ResourceResponse<T>) => { },
        ...settings.reduce((acc: RequestSettings<T>, setting: RequestSettings<T>) => ({
            ...acc,
            ...setting
        }), {})
    };

    const handleShouldNotifyOnSuccess = (response: ResourceResponse<T>) => (
        typeof shouldNotifyOnSuccess === 'function'
            ? shouldNotifyOnSuccess(response)
            : shouldNotifyOnSuccess
    );

    return {
        ...settingsWithDefaults,
        shouldNotifyOnReject,
        handleShouldNotifyOnSuccess,
        shouldNotify: (response: ResourceResponse<T>) => [
            shouldNotifyOnReject,
            handleShouldNotifyOnSuccess
        ].some(fn => fn(response)),
    };
};
